/*
 * Copyright (c) 2025 AirAsia Berhad (Malaysia). All rights reserved.
 *
 * This software is proprietary and confidential. Unauthorized copying of this file,
 * via any medium, is strictly prohibited. Do not distribute, copy, modify, or reuse
 * any part of this code without explicit written permission from AirAsia Move.
 *
 * For internal use only.
 */

import { DateTimeParser } from '@airasia-rn/ui';
import {
  parse,
  isValid,
  addDays,
  addMonths,
  endOfDay,
  isBefore,
  differenceInMonths,
  differenceInYears,
  format,
  parseISO,
  startOfDay,
} from 'date-fns';

import { describeError } from './describeError';
const EXPIRING_MONTHS_THRESHOLD = 6;
/** Age threshold for parental consent (under 18 = minor). */
export const MINOR_AGE_THRESHOLD = 18;
const DATE_PICKER_OUTPUT_FORMAT = "EEE MMM dd yyyy HH:mm:ss 'GMT'xx";
const FLEXIBLE_DATE_FORMATS = [
  DATE_PICKER_OUTPUT_FORMAT,
  'dd/MM/yyyy',
  'yyyy-MM-dd',
  'ddMMyyyy',
] as const;

function tryParseWithFormats(value: string): Date | null {
  for (const inputFormat of FLEXIBLE_DATE_FORMATS) {
    const parsed = parse(value, inputFormat, new Date());
    if (isValid(parsed)) return parsed;
  }
  return null;
}

export const DateUtils = {
  /**
   * Parse a string into a Date using the given input format.
   */
  parseDate(
    value: string | null | undefined,
    inputFormat: string = DATE_PICKER_OUTPUT_FORMAT,
  ): Date | null {
    if (!value) return null;

    const parsed = parse(value, inputFormat, new Date());
    return isValid(parsed) ? parsed : null;
  },

  /**
   * Parse and format in one step.
   */
  parseAndFormatDate(
    value: string | null | undefined,
    inputFormat: string = DATE_PICKER_OUTPUT_FORMAT,
  ): string | null {
    const parsed = this.parseDate(value, inputFormat);
    return parsed
      ? DateTimeParser.parseMilliSecondsString(
          parsed.getTime().toString(),
        ).formatDate()
      : null;
  },

  /**
   * Calculate months between two dates.
   */
  monthsBetweenDates(startDate: Date | null, endDate: Date | null): number {
    if (!startDate || !endDate) return 0;
    if (isNaN(startDate.getTime()) || isNaN(endDate.getTime())) return 0;

    return Math.abs(differenceInMonths(endDate, startDate));
  },

  /**
   * Start of the current local calendar day (midnight).
   */
  getStartOfToday(): Date {
    return startOfDay(new Date());
  },

  /**
   * Start of tomorrow's local calendar day (midnight).
   */
  getStartOfTomorrow(): Date {
    return addDays(this.getStartOfToday(), 1);
  },

  /**
   * End of the current local calendar day (inclusive max for DOB pickers).
   */
  getEndOfToday(): Date {
    return endOfDay(new Date());
  },

  /**
   * True when the calendar date is strictly before today.
   */
  isBeforeToday(value: Date | null | undefined): boolean {
    if (!value || isNaN(value.getTime())) return false;
    return isBefore(startOfDay(value), this.getStartOfToday());
  },

  /**
   * Parse travel-document expiry values from picker, web input, or API formats.
   */
  parseExpiryDateInput(value?: string | null): Date | null {
    return this.parseFlexibleDate(value);
  },

  /**
   * True when value parses to a future calendar date (tomorrow or later).
   */
  isFutureDate(value?: string | Date | null): boolean {
    const parsed = this.parseFlexibleDate(value);
    if (!parsed) return false;
    return isBefore(this.getStartOfToday(), startOfDay(parsed));
  },

  /**
   * True when value parses to a future calendar date (tomorrow or later).
   */
  isValidExpiryDate(value?: string | null): boolean {
    return this.isFutureDate(value);
  },

  /**
   * Check if a date is expired (past today).
   */
  isExpired(value: string | Date | null | undefined): boolean {
    const expiry = typeof value === 'string' ? this.parseDate(value) : value;
    if (!expiry) return false;

    const now = new Date();

    return now > expiry;
  },

  /**
   * Check if a date is within N months from now (default 6).
   */
  isWithinMonthsFromNow(
    value: string | Date | null | undefined,
    threshold: number = EXPIRING_MONTHS_THRESHOLD,
  ): boolean {
    const expiry = typeof value === 'string' ? this.parseDate(value) : value;
    if (!expiry) return false;

    const now = new Date();

    // Already expired
    if (expiry < now) return false;

    // Compute target date safely using addMonths
    const target = addMonths(now, threshold);

    // Check if expiry is before or equal to target
    return isBefore(expiry, target) || expiry.getTime() === target.getTime();
  },

  formatStringToDDMMYYYY(
    value: string | null | undefined,
    inputFormat: string = "EEE MMM dd yyyy HH:mm:ss 'GMT'xx",
  ): string {
    if (!value) return '';

    const sanitizedValue = value.replace(/\s*\(.*\)$/, '');
    const date = this.parseDate(sanitizedValue, inputFormat);

    if (!date || isNaN(date.getTime())) return '';

    const day = date.getDate().toString().padStart(2, '0');
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const year = date.getFullYear().toString();

    return `${day}${month}${year}`;
  },

  /**
   * Format date to DDMMYYYY format.
   */
  formatToDDMMYYYY(date: Date): string {
    if (!date || isNaN(date.getTime())) return '';

    // Format to DDMMYYYY
    const day = date.getDate().toString().padStart(2, '0');
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const year = date.getFullYear().toString();

    return `${day}${month}${year}`;
  },

  /**
   * Format date of birth from various formats to "dd MMM yyyy" or any format.
   * Handles DDMMYYYY format (e.g., "01021993") and ISO date formats.
   */
  formatUserDate(
    dateString: string,
    formatStr: string = 'dd MMM yyyy',
  ): string {
    try {
      // Check if the date is in DDMMYYYY format (8 digits)
      if (dateString.length === 8 && /^\d{8}$/.test(dateString)) {
        const day = dateString.substring(0, 2);
        const month = dateString.substring(2, 4);
        const year = dateString.substring(4, 8);

        // Create date in YYYY-MM-DD format for parsing
        const isoDateString = `${year}-${month}-${day}`;
        const date = parseISO(isoDateString);

        if (isValid(date)) {
          return format(date, formatStr);
        }
      }

      // Try parsing as ISO date (existing logic)
      const date = parseISO(dateString);
      if (isValid(date)) {
        return format(date, formatStr);
      }
    } catch (error) {
      console.warn('Invalid date format:', dateString, describeError(error));
    }

    // Return original string if parsing fails
    return dateString;
  },

  /**
   * Format an API timestamp (ISO 8601 or Unix ms) to "d MMM yyyy 'at' h:mm a"
   * e.g. "2025-12-08T15:00:00.000Z" or 1765191600000 → "8 Dec 2025 at 3:00 PM"
   */
  formatSecurityActivityTimestamp(
    value: string | number | null | undefined,
  ): string {
    if (value == null || value === '') return '';
    try {
      let date: Date;
      if (typeof value === 'number') {
        date = new Date(value);
      } else {
        const trimmed = String(value).trim();
        if (/^\d+$/.test(trimmed)) {
          date = new Date(Number(trimmed));
        } else {
          date = parseISO(trimmed);
        }
      }
      if (!isValid(date)) return '';
      return format(date, "d MMM yyyy 'at' h:mm a");
    } catch {
      return '';
    }
  },

  /**
   * Format an API timestamp to time only "h:mm a" (e.g. "3:30 PM") for recent security activity detail access time.
   */
  formatRecentSecurityActivityAccessTime(
    value: string | number | null | undefined,
  ): string {
    if (value == null || value === '') return '';
    try {
      let date: Date;
      if (typeof value === 'number') {
        date = new Date(value);
      } else {
        const trimmed = String(value).trim();
        if (/^\d+$/.test(trimmed)) {
          date = new Date(Number(trimmed));
        } else {
          date = parseISO(trimmed);
        }
      }
      if (!isValid(date)) return '';
      return format(date, 'h:mm a');
    } catch {
      return '';
    }
  },

  /**
   * Parse a date string in ddMMyyyy format to a Date object
   * @param dateString - Date string in ddMMyyyy format (e.g., "19112038")
   * @returns Parsed Date object or null if invalid
   */
  parseDDMMYYYY(dateString: string): Date | null {
    if (dateString.length === 8 && /^\d{8}$/.test(dateString)) {
      const parsed = parse(dateString, 'ddMMyyyy', new Date());
      return isValid(parsed) ? parsed : null;
    }
    return null;
  },
  /**
   * Convert card expiry MMYYYY → "Expires MM/YY"
   * Example: "082030" → "Expires 08/30"
   */
  formatCardExpiry(expiry?: string | null): string {
    if (!expiry || expiry.length < 6) return '';

    const month = expiry.slice(0, 2);
    const year = expiry.slice(-2);

    return `${month}/${year}`;
  },
  parseFlexibleDate(value: string | Date | null | undefined): Date | null {
    if (!value) return null;

    if (value instanceof Date) {
      return isNaN(value.getTime()) ? null : value;
    }

    const str = String(value).trim();
    if (!str) return null;

    const fromKnownFormats = tryParseWithFormats(str);
    if (fromKnownFormats) return fromKnownFormats;

    const isoParsed = parseISO(str);
    if (isValid(isoParsed)) return isoParsed;

    const nativeParsed = new Date(str);
    return isNaN(nativeParsed.getTime()) ? null : nativeParsed;
  },
  parseAndFormatFlexibleDate(
    value: string | Date | null | undefined,
    formatStr: string = 'dd/MM/yyyy',
  ): string {
    const parsed = this.parseFlexibleDate(value);

    if (!parsed || isNaN(parsed.getTime())) return '';

    return format(parsed, formatStr);
  },

  /**
   * True when DOB parses and age is under {@link MINOR_AGE_THRESHOLD}.
   * Used for parental consent on signup and guardian details elsewhere.
   */
  isMinorAge(
    value: string | Date | null | undefined,
    threshold: number = MINOR_AGE_THRESHOLD,
  ): boolean {
    const dob = this.parseFlexibleDate(value);
    if (!dob || isNaN(dob.getTime())) return false;
    return differenceInYears(new Date(), dob) < threshold;
  },
};
